// profile-xero.component.ts
import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { AuthProvider, XeroTenant } from 'src/app/models';
import { User } from 'src/app/models/users.model';
import { SharedService } from 'src/app/shared/services/shared.service';
import { XeroAuthService } from 'src/app/shared/services/xero-auth.service';
import { XeroBankFeedService } from 'src/app/shared/services/xero-bank-feed.service';
import { XeroUserService } from 'src/app/shared/services/xero-user.service';
import { XeroFeedConnectionsComponent } from 'src/app/shared/ui/xero-feed-connections/xero-feed-connections.component';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { sleep } from 'src/app/shared/utils/helper.utils';

@Component({
  selector: 'app-profile-xero',
  templateUrl: './profile-xero.component.html',
  styleUrls: ['./profile-xero.component.scss'],
})
export class ProfileXeroComponent implements OnInit {
  user: User = {} as User;
  xeroProvider: AuthProvider | null = null;
  isLoading = true;
  selectedTenant: XeroTenant | null = null;
  /** Bank-feeds UI (feed connections, statement copy) only shows for flagged users */
  bankFeedsEnabled = false;

  constructor(
    private ss: SharedService,
    private firestore: AngularFirestore,
    private xeroAuthService: XeroAuthService,
    private xeroUserService: XeroUserService,
    private bankFeed: XeroBankFeedService,
    private featureFlag: FeatureFlagService,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService,
  ) {}

  async ngOnInit() {
    this.user = await this.ss.getUser();
    [this.bankFeedsEnabled] = await Promise.all([this.featureFlag.isEnabled('xeroBankFeedsEnabled'), this.loadXeroProvider()]);
    this.isLoading = false;
  }

  async loadXeroProvider() {
    if (!this.user?.uid) return;

    try {
      const providerId = `${this.user.uid}_xero`;
      const doc = await this.firestore.doc<AuthProvider>(`authProviders/${providerId}`).get().toPromise();
      this.xeroProvider = doc.exists ? (doc.data() as AuthProvider) : null;

      // Select first tenant by default if available
      if (this.xeroProvider?.tenants && this.xeroProvider.tenants.length > 0) {
        this.selectedTenant = this.xeroProvider.tenants[0];
      }
    } catch (error) {
      console.error('Error loading Xero provider:', error);
      this.xeroProvider = null;
    }
  }

  async connect() {
    try {
      this.isLoading = true;
      await this.xeroAuthService.initiateXeroConnect();
      // Keep spinner visible for 2s to bridge the gap while browser redirects to Xero
      await sleep(2000);
    } catch (error) {
      this.toastrService.danger('Failed to connect to Xero', 'Error');
      console.error('Error connecting to Xero:', error);
      this.isLoading = false;
    }
  }

  async disconnect() {
    try {
      this.isLoading = true;
      await this.xeroAuthService.disconnect(this.user.uid);

      // Wait for Firestore to reflect the disconnection (max 10s)
      const disconnected = await this.waitForDisconnection();

      if (!disconnected) {
        console.warn('Disconnect polling timed out - forcing refresh');
      }

      // Conversion rows cache the connection state to gate their Xero button
      this.bankFeed.refreshConnection();

      // Reload the provider state
      await this.loadXeroProvider();

      this.toastrService.success('Successfully disconnected from Xero', 'Success');
    } catch (error) {
      this.toastrService.danger('Failed to disconnect from Xero', 'Error');
      console.error('Error disconnecting from Xero:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Wait for Firestore to reflect the disconnection using a real-time listener.
   * Returns true if disconnection detected, false if timeout reached.
   */
  private waitForDisconnection(): Promise<boolean> {
    return new Promise((resolve) => {
      const providerId = `${this.user.uid}_xero`;
      const timeout = 10000; // 10 second max wait

      // Subscribe to real-time updates
      const subscription = this.firestore
        .doc<AuthProvider>(`authProviders/${providerId}`)
        .valueChanges()
        .subscribe(
          (data) => {
            // Success: doc deleted or marked inactive
            if (!data || !data.isActive) {
              subscription.unsubscribe();
              resolve(true);
            }
          },
          (error) => {
            console.error('Error watching provider state:', error);
            subscription.unsubscribe();
            resolve(false);
          },
        );

      // Safety timeout - unsubscribe and resolve after 10s
      setTimeout(() => {
        subscription.unsubscribe();
        resolve(false);
      }, timeout);
    });
  }

  async getUser() {
    try {
      this.isLoading = true;
      await this.xeroUserService.getUserInfo();
    } finally {
      this.isLoading = false;
    }
  }

  async refresh() {
    try {
      this.isLoading = true;
      this.user = await this.ss.getUser();
      await this.loadXeroProvider();
    } catch (error) {
      this.toastrService.danger('Failed to load user data', 'Error');
      console.error('Error loading user:', error);
    } finally {
      this.isLoading = false;
    }
  }

  get isXeroConnected() {
    return !!this.xeroProvider && this.xeroProvider.isActive && !this.needsReconnect;
  }

  /** Provider exists but its tokens are dead — user must go through OAuth again */
  get needsReconnect() {
    return !!this.xeroProvider && (this.xeroProvider.needsReauth || !this.xeroProvider.isActive);
  }

  get hasMultipleTenants() {
    return this.xeroProvider?.tenants && this.xeroProvider.tenants.length > 1;
  }

  openFeedConnections(tenant: XeroTenant) {
    this.dialogService.open(XeroFeedConnectionsComponent, {
      context: {
        tenant: tenant,
      },
    });
  }
}
